import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Shield } from 'lucide-react';
import { Button } from './ui/button';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'About Us', path: '/about' },
    { name: 'Service Areas', path: '/service-areas' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
      }`}
      data-testid="navbar"
    >
      <div className="container-main">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 bg-[#2E7D32] rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl sm:text-2xl font-bold text-[#1A1C1A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Primes Pick
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'text-[#2E7D32] bg-[#E8F5E9]'
                    : 'text-[#52525B] hover:text-[#2E7D32] hover:bg-[#E8F5E9]/50'
                }`}
                data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <a href="tel:+18883568891" className="btn-accent text-sm" data-testid="call-now-btn">
              <Phone className="w-4 h-4" />
              Call Now
            </a>
            <Link to="/contact">
              <Button className="btn-primary text-sm" data-testid="get-quote-btn">
                Get Free Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-[#E8F5E9] transition-colors"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? (
              <X className="w-6 h-6 text-[#1A1C1A]" />
            ) : (
              <Menu className="w-6 h-6 text-[#1A1C1A]" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden mobile-menu-enter border-t border-[#E8F5E9]" data-testid="mobile-menu">
            <nav className="py-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-4 py-3 rounded-lg font-medium transition-colors duration-200 ${
                    isActive(link.path)
                      ? 'text-[#2E7D32] bg-[#E8F5E9]'
                      : 'text-[#52525B] hover:text-[#2E7D32] hover:bg-[#E8F5E9]/50'
                  }`}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>
            <div className="py-4 space-y-3 border-t border-[#E8F5E9]">
              <a
                href="tel:+18883568891"
                className="btn-accent w-full text-center block"
                data-testid="mobile-call-btn"
              >
                <Phone className="w-4 h-4 inline mr-2" />
                Call Now: +1-888-356-8891
              </a>
              <Link to="/contact" className="block">
                <Button className="btn-primary w-full" data-testid="mobile-quote-btn">
                  Get Free Quote
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
